import { formatCurrency, pricing } from './pricing'

export type ServiceId =
  | 'mobile-notary'
  | 'e-notary'
  | 'loan-signing'
  | 'trust-estate-signing'
  | 'i9-verification'
  | 'field-inspections'
  | 'certified-copies'
  | 'document-courier'
  | 'apostille-assistance'

export interface Service {
  id: ServiceId
  name: string
  shortName: string
  eyebrow: string
  summary: string
  overview: string
  audience: string[]
  expectations: string[]
  pricingSummary: string
  quoteRequired: boolean
  caution?: string
}

export const services: Service[] = [
  {
    id: 'mobile-notary',
    name: 'Mobile Notary Services',
    shortName: 'Mobile Notary',
    eyebrow: 'Notarization brought to you',
    summary:
      'Convenient in-person notarization at your home, office, care facility, or agreed public location.',
    overview:
      'Michael travels to an agreed location, checks identification, witnesses signatures when required, and completes the appropriate notarial certificate for documents you provide.',
    audience: ['Individuals and families', 'Businesses', 'Hospitals and care facilities'],
    expectations: [
      'Have the complete, unsigned document ready unless instructed otherwise.',
      'Each signer should have acceptable, current identification.',
      'All required signers and witnesses must be available at the agreed time.',
    ],
    pricingSummary: `${formatCurrency(pricing.traditionalNotary.perStamp)} per stamp + ${formatCurrency(pricing.traditionalNotary.perSigner)} per signer, plus travel`,
    quoteRequired: false,
  },
  {
    id: 'e-notary',
    name: 'E-Notary Services',
    shortName: 'E-Notary',
    eyebrow: 'Remote online convenience',
    summary:
      'Electronic notarization support through an approved online platform when the document and circumstances are eligible.',
    overview:
      'E-notary sessions are completed online using platform identity verification and secure audiovisual communication. Eligibility and platform requirements are confirmed before scheduling.',
    audience: ['Remote signers', 'Traveling professionals', 'Time-sensitive personal matters'],
    expectations: [
      'A reliable internet connection, camera, microphone, and approved identification are required.',
      'The document must be suitable for electronic notarization.',
      'Platform charges may be added to the stated service fees.',
    ],
    pricingSummary: `${formatCurrency(pricing.eNotary.perStamp)} per stamp + ${formatCurrency(pricing.eNotary.perSigner)} per signer`,
    quoteRequired: false,
    caution: pricing.eNotary.platformFeeNote,
  },
  {
    id: 'loan-signing',
    name: 'Loan Signing Services',
    shortName: 'Loan Signings',
    eyebrow: 'Careful document execution',
    summary:
      'Professional signing support for real estate and lending document packages.',
    overview:
      'Michael provides a structured signing appointment, presents the documents supplied by the title, escrow, or lending professional, and returns the completed package as directed.',
    audience: ['Title and escrow professionals', 'Lenders', 'Borrowers and sellers'],
    expectations: [
      'The hiring party supplies the final document package and return instructions.',
      'Signers should bring acceptable identification and allow adequate time.',
      'Questions about loan terms must be directed to the lender or title professional.',
    ],
    pricingSummary: `${formatCurrency(pricing.loanSigning)} flat fee`,
    quoteRequired: false,
  },
  {
    id: 'trust-estate-signing',
    name: 'Trust & Estate Signing Services',
    shortName: 'Trust & Estate Signings',
    eyebrow: 'Calm, organized signing support',
    summary:
      'Notarization and signing support for estate documents prepared by you or your attorney.',
    overview:
      'Michael helps coordinate a careful signing session for prepared trust and estate documents. He confirms identity and performs requested notarial acts but does not draft, select, or explain legal forms.',
    audience: ['Individuals and families', 'Estate planning law firms', 'Care facilities'],
    expectations: [
      'Documents must be prepared in advance by the client or the client’s attorney.',
      'Required witnesses should be identified before the appointment.',
      'Questions about legal effect must be directed to an attorney.',
    ],
    pricingSummary: `${formatCurrency(pricing.trustEstateSigning)} flat fee`,
    quoteRequired: false,
    caution: 'Notarization and signing support only. Legal advice is not provided.',
  },
  {
    id: 'i9-verification',
    name: 'I-9 Verification Support',
    shortName: 'I-9 Verification',
    eyebrow: 'Authorized representative support',
    summary:
      'In-person document review and authorized representative support for employer-directed Form I-9 completion.',
    overview:
      'When an employer permits an authorized representative, Michael can meet with the employee to inspect original documents and complete the employer-directed verification steps.',
    audience: ['Remote employers', 'New employees', 'HR teams'],
    expectations: [
      'The employer must provide instructions and authorize the representative role.',
      'The employee must present original acceptable documents.',
      'This service does not imply government employment, endorsement, or immigration advice.',
    ],
    pricingSummary: `${formatCurrency(pricing.i9Verification)} + travel`,
    quoteRequired: false,
  },
  {
    id: 'field-inspections',
    name: 'Field Inspection Services',
    shortName: 'Field Inspections',
    eyebrow: 'Reliable on-site reporting',
    summary:
      'On-site observation and documentation support for occupancy, insurance, commercial, delinquency, and equipment assignments.',
    overview:
      'Michael follows the hiring company’s scope, visits the assigned location, records permitted observations, and returns the requested report and photographs through the designated process.',
    audience: ['Inspection companies', 'Insurers', 'Equipment lessors and businesses'],
    expectations: [
      'The hiring party provides the assignment scope and reporting requirements.',
      'Pricing varies by inspection type and travel area.',
      'No appraisal, engineering, or code-compliance opinion is provided.',
    ],
    pricingSummary: 'From $20; varies by inspection type and travel',
    quoteRequired: true,
  },
  {
    id: 'certified-copies',
    name: 'Certified Copy Support',
    shortName: 'Certified Copies',
    eyebrow: 'Document copy assistance',
    summary:
      'Certified-copy support for eligible documents when permitted by applicable Arkansas law.',
    overview:
      'Michael reviews the document type and requested use before confirming whether a copy certification can be performed. Some vital, public, or recordable documents must be obtained from the issuing agency instead.',
    audience: ['Individuals and families', 'Businesses', 'Administrative offices'],
    expectations: [
      'Eligibility depends on the document type and applicable law.',
      'Bring the original document when required.',
      'Agency-issued certified copies may need to be requested directly from the custodian.',
    ],
    pricingSummary: `${formatCurrency(pricing.certifiedCopy)} per eligible document + travel`,
    quoteRequired: false,
    caution: 'Not every document can be copy-certified by a notary.',
  },
  {
    id: 'document-courier',
    name: 'Document Courier Services',
    shortName: 'Document Courier',
    eyebrow: 'Secure point-to-point support',
    summary:
      'Local pickup and delivery coordination for business and personal documents.',
    overview:
      'Michael coordinates an agreed pickup and delivery route, confirms handling instructions, and communicates completion. Sensitive material should be packaged appropriately by the client.',
    audience: ['Law firms and title offices', 'Local businesses', 'Individuals'],
    expectations: [
      'Pickup, destination, timing, and recipient details are confirmed in advance.',
      'The client remains responsible for document contents and packaging.',
      'Travel pricing applies; complex or time-sensitive routes require a custom quote.',
    ],
    pricingSummary: 'Applicable travel fee; custom quote for complex routes',
    quoteRequired: true,
  },
  {
    id: 'apostille-assistance',
    name: 'Apostille Assistance',
    shortName: 'Apostille Assistance',
    eyebrow: 'Administrative submission support',
    summary:
      'Document handling, submission support, and courier coordination for the apostille process.',
    overview:
      'Michael can help organize eligible documents, coordinate delivery or submission, and return completed materials. Apostilles are issued by the appropriate government authority—not by Square & Compass Notary.',
    audience: ['Individuals with international document needs', 'Families', 'Businesses'],
    expectations: [
      'Document type, destination country, and issuing authority are reviewed first.',
      'Government processing times and agency fees are outside Michael’s control.',
      'Full handling and limited per-document support are available.',
    ],
    pricingSummary: '$200 + $20/document full package, or $20/document limited assistance + travel',
    quoteRequired: true,
    caution: 'Square & Compass Notary assists with the process and does not issue apostilles.',
  },
]

export const serviceById = (id: string) => services.find((service) => service.id === id)
